import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { RootState } from "../../store";
import Button from "../../components/UI/Buttons/Button/Button";
import TextField from "../../components/TextField/TextField";
import { selectTokenUserId } from "../../store/auth-slice";
import {
  selectAdvisorContextState,
  selectCanActivateAvailability,
} from "../../store/advisorContext-slice";
import {
  AdvisorProfileFetchResult,
  fetchAdvisorProfile,
} from "../../store/advisorContext-actions";
import {
  buildImagesCreate,
  buildImagesUpdate,
  createAdvisorProfile,
  getAdvisoryAreas,
  PendingMediaFile,
  setAdvisorAvailability,
  updateAdvisorProfile,
  updateAdvisorWorkingHours,
} from "../../services/advisorProfileService";
import {
  AdvisorProfileFileResponse,
  AdvisorProfileRequest,
  AdvisorProfileResponse,
  AdvisorWorkingHoursEntry,
  AdvisoryAreaResponse,
} from "../../types/advisorProfile.types";
import {
  GENERIC_LOAD_ERROR,
  PROFILE_GONE_ERROR,
  isProfileAlreadyExists,
  mapAvailabilityError,
  mapProfileSaveError,
  mapWorkingHoursError,
  shouldRefetchAfterError,
} from "../../utils/advisorProfileErrors";
import WorkingHoursEditor, {
  HoursDraft,
  buildHoursDraft,
  minutesOfDay,
} from "../VolunteerProfilePage/WorkingHoursEditor";
import MediaManager from "./MediaManager";
import styles from "./AdvisorProfilePage.module.scss";

// ITAM-2887: the advisor's self-managed profile, ported from the dev branch's
// pharmacist profile page (ITAM-2622, 3.9).
//
// Four top-level states (loading, load error, 404 -> create, profile exists)
// plus the enrollment sub-states of the availability block. The own user id
// comes EXCLUSIVELY from the JWT, never from a route or a form field.
//
// Every mutating control carries a pending lock and disables while its request
// is in flight, so a double-click fires exactly ONE request.

const MAX_NAME = 64;
const MAX_CITY = 128;
const MAX_PHONE = 64;
const STATUS_REFETCH_MS = 30000;

const AVAILABILITY_LABEL: Record<string, string> = {
  FREE: "Slobodan",
  IN_CALL: "U pozivu",
  UNAVAILABLE: "Nedostupan",
};

const PROFILE_EXISTS_NOTICE =
  "Profil već postoji, učitali smo postojeće podatke.";
const NO_ASSOCIATIONS_NOTICE =
  "Još niste dodati ni u jedno udruženje. Udruženje vas dodaje putem vaše e-mail adrese.";
const AREAS_UNAVAILABLE = "Oblasti trenutno nisu dostupne";

interface IdentityDraft {
  firstName: string;
  secondName: string;
  city: string;
  phoneNumber: string;
  biography: string;
}

interface AccountNames {
  firstName: string;
  secondName: string;
}

/**
 * A loaded profile is the only source for an existing row; the account names
 * only prefill the create form, where there is no row to read from.
 */
const identityFrom = (
  profile: AdvisorProfileResponse | null,
  account?: AccountNames
): IdentityDraft =>
  profile != null
    ? {
        firstName: profile.firstName ?? "",
        secondName: profile.secondName ?? "",
        city: profile.city ?? "",
        phoneNumber: profile.phoneNumber ?? "",
        biography: profile.biography ?? "",
      }
    : {
        firstName: account?.firstName ?? "",
        secondName: account?.secondName ?? "",
        city: "",
        phoneNumber: "",
        biography: "",
      };

const areaIdsFrom = (profile: AdvisorProfileResponse | null): number[] =>
  (profile?.advisoryAreas ?? []).map((area) => area.id);

type IdentityErrors = Partial<Record<keyof IdentityDraft, string>>;

const IDENTITY_RULES: Array<{
  key: keyof IdentityDraft;
  max: number;
  requiredMessage: string | null;
}> = [
  { key: "firstName", max: MAX_NAME, requiredMessage: "Unesite ime." },
  { key: "secondName", max: MAX_NAME, requiredMessage: "Unesite prezime." },
  { key: "city", max: MAX_CITY, requiredMessage: null },
  { key: "phoneNumber", max: MAX_PHONE, requiredMessage: null },
];

/** Ime and prezime non-blank; every bounded field within its backend limit. */
const validateIdentity = (draft: IdentityDraft): IdentityErrors => {
  const errors: IdentityErrors = {};
  IDENTITY_RULES.forEach(({ key, max, requiredMessage }) => {
    const value = draft[key].trim();
    if (!value) {
      if (requiredMessage) {
        errors[key] = requiredMessage;
      }
    } else if (value.length > max) {
      errors[key] = `Najviše ${max} karaktera.`;
    }
  });
  return errors;
};

/**
 * At least one VALID interval, not merely a non-empty schedule. Decided on
 * parsed minutes, so stored junk can never pass as a usable window.
 */
const hasUsableSchedule = (
  entries: AdvisorWorkingHoursEntry[] | null | undefined
): boolean =>
  (entries ?? []).some((entry) => {
    const from = minutesOfDay(entry.availableFrom);
    const until = minutesOfDay(entry.availableUntil);
    return from != null && until != null && from < until;
  });

type AdvisoryAreasState =
  | { status: "loading" }
  | { status: "loaded"; areas: AdvisoryAreaResponse[] }
  | { status: "error" };

const AdvisorProfileEditor: React.FC = () => {
  const dispatch = useDispatch();
  const userId = useSelector(selectTokenUserId);
  const { profile, status, loadError } = useSelector(selectAdvisorContextState);
  const canActivate = useSelector(selectCanActivateAvailability);
  const accountFirstName = useSelector(
    (state: RootState) => state.auth.userInfo?.firstName ?? ""
  );
  const accountSecondName = useSelector(
    (state: RootState) => state.auth.userInfo?.secondName ?? ""
  );

  const [identity, setIdentity] = useState<IdentityDraft>(() =>
    identityFrom(profile, {
      firstName: accountFirstName,
      secondName: accountSecondName,
    })
  );
  const [identityErrors, setIdentityErrors] = useState<IdentityErrors>({});
  const [selectedAreaIds, setSelectedAreaIds] = useState<number[]>(() =>
    areaIdsFrom(profile)
  );
  const [advisoryAreas, setAdvisoryAreas] = useState<AdvisoryAreasState>({
    status: "loading",
  });
  const [keptMedia, setKeptMedia] = useState<AdvisorProfileFileResponse[]>(
    profile?.images ?? []
  );
  const [pendingMedia, setPendingMedia] = useState<PendingMediaFile[]>([]);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [hoursDraft, setHoursDraft] = useState<HoursDraft>(() =>
    buildHoursDraft(profile?.workingHours)
  );

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [hoursError, setHoursError] = useState<string | null>(null);
  const [hoursSaved, setHoursSaved] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(
    null
  );
  const [availabilityNotice, setAvailabilityNotice] = useState<string | null>(
    null
  );

  // The advisory-area catalog is fetched ONCE per mount (the editor is keyed
  // on the user id) and cached here. A failure is not retried: the row says
  // the areas are unavailable and the save leaves the stored areas alone.
  useEffect(() => {
    if (userId == null) {
      return;
    }
    const controller = new AbortController();
    getAdvisoryAreas(controller.signal)
      .then((areas) => {
        if (!controller.signal.aborted) {
          setAdvisoryAreas({ status: "loaded", areas });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setAdvisoryAreas({ status: "error" });
        }
      });
    return () => controller.abort();
  }, [userId]);

  // Re-seeding is SCOPED to what a mutation owns, and every call site is
  // explicit, because the two ways of getting here need opposite things:
  //
  //  - a background arrival (the 30 s status refetch) must NOT touch the editor,
  //    or it would delete whatever the advisor is halfway through typing;
  //  - the refetch that FOLLOWS a mutation must rebase the editor on server
  //    truth, or the fields keep showing what was sent rather than what was
  //    stored, and `pendingMedia`, left un-cleared, is uploaded a SECOND time
  //    by the next save.
  //
  // Keying the effect below on `profile.id` gets the first half right and the
  // second half wrong on its own, since an update to an existing profile returns
  // the same id and the effect never re-runs. Hence the explicit seeders. The
  // advisory areas ride with the identity because the profile save owns them.
  const seedIdentityAndMedia = useCallback((next: AdvisorProfileResponse) => {
    setIdentity(identityFrom(next));
    setSelectedAreaIds(areaIdsFrom(next));
    setKeptMedia(next.images ?? []);
    setPendingMedia([]);
  }, []);
  const seedHours = useCallback((next: AdvisorProfileResponse) => {
    setHoursDraft(buildHoursDraft(next.workingHours));
  }, []);

  // ...and re-seeding is CONDITIONAL on the reconciliation GET having found a
  // profile. A 404 there proves only that the server holds nothing; it says
  // nothing about what the advisor typed. Adopting that null would empty the
  // onboarding form and drop the selected files underneath the very error
  // message asking them to try again. The id-keyed effect below still owns the
  // legitimate empty first render.
  const adopt = useCallback(
    (
      result: AdvisorProfileFetchResult,
      seed: (next: AdvisorProfileResponse) => void
    ) => {
      if (result.ok && result.profile != null) {
        seed(result.profile);
      }
    },
    []
  );

  // Initial load, and any change of WHICH profile is loaded (404 -> created).
  // Deliberately id-keyed, so background arrivals are inert.
  const profileId = profile?.id ?? null;
  useEffect(() => {
    if (profile == null) {
      // A CONFIRMED absence: a failed GET leaves the previous profile in place,
      // so reaching null always means the server answered and found nothing.
      // Split the state by who owns it: media rows belonged to the profile
      // that is gone and must not be advertised, since the create path sends
      // `pendingMedia` only and would silently drop them; identity, areas,
      // pending uploads and the hours draft are the advisor's unsaved work and
      // stay. This lives in the effect, not in the mutation handlers, because
      // null is also reached by the 30 s poll and the availability re-read.
      setKeptMedia((current) => (current.length === 0 ? current : []));
      return;
    }
    seedIdentityAndMedia(profile);
    seedHours(profile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  // The create form prefills the names from the account. `userInfo` can
  // arrive after the first render, so a name is filled only while its field
  // is still blank: anything the advisor already typed wins.
  useEffect(() => {
    if (profileId != null) {
      return;
    }
    setIdentity((prev) => {
      const firstName = prev.firstName || accountFirstName;
      const secondName = prev.secondName || accountSecondName;
      return firstName === prev.firstName && secondName === prev.secondName
        ? prev
        : { ...prev, firstName, secondName };
    });
  }, [profileId, accountFirstName, accountSecondName]);

  const refetch = useCallback(async (): Promise<AdvisorProfileFetchResult> => {
    if (userId == null) {
      return { ok: false };
    }
    return dispatch<any>(
      fetchAdvisorProfile(userId)
    ) as Promise<AdvisorProfileFetchResult>;
  }, [dispatch, userId]);

  // A light 30 s refetch while the page is visible, no global polling. It is
  // also what picks up a newly added association.
  useEffect(() => {
    if (userId == null || profile == null) {
      return;
    }
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        dispatch<any>(fetchAdvisorProfile(userId));
      }
    }, STATUS_REFETCH_MS);
    return () => clearInterval(interval);
  }, [dispatch, userId, profile]);

  const hasSchedule = hasUsableSchedule(profile?.workingHours);
  const enrolled = profile?.availability != null;
  const paused = profile?.paused === true;

  // ONE gate for BOTH `available=true` controls, initial activation and the
  // paused-state resume, so the two can never drift apart: capability plus an
  // existing profile (`canActivate`), a usable schedule, and no availability
  // mutation in flight. Pause is never gated by any of this; going
  // unavailable is always safe.
  const canSubmitAvailabilityTrue =
    canActivate && hasSchedule && !savingAvailability;
  const mediaChanged =
    pendingMedia.length > 0 ||
    (profile?.images?.length ?? 0) !== keptMedia.length;

  const buildRequest = useCallback(
    (forCreate: boolean): AdvisorProfileRequest | null => {
      if (userId == null) {
        return null;
      }
      // The update overwrites every scalar unconditionally, so all five go.
      const base: AdvisorProfileRequest = {
        userId,
        firstName: identity.firstName.trim(),
        secondName: identity.secondName.trim(),
        biography: identity.biography.trim(),
        city: identity.city.trim(),
        phoneNumber: identity.phoneNumber.trim(),
      };
      if (!forCreate && profile?.id != null) {
        base.id = profile.id;
      }
      // Without the catalog the advisor could not have edited the areas, so
      // the key is omitted and the stored areas stay untouched. With it, the
      // selection is sent as is ([] clears).
      if (advisoryAreas.status === "loaded") {
        base.advisoryAreaIds = selectedAreaIds;
      }
      // A profile save must NEVER replace working hours: omitting the key
      // leaves the schedule alone, while sending [] would clear it.
      return base;
    },
    [advisoryAreas.status, identity, profile, selectedAreaIds, userId]
  );

  const submitProfile = async (forCreate: boolean) => {
    if (savingProfile || userId == null) {
      return;
    }
    const errors = validateIdentity(identity);
    setIdentityErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    const request = buildRequest(forCreate);
    if (!request) {
      return;
    }
    setSavingProfile(true);
    setProfileError(null);
    try {
      if (forCreate) {
        const { images, files } = buildImagesCreate(pendingMedia);
        await createAdvisorProfile({ ...request, images }, files);
      } else {
        const { images, files } = buildImagesUpdate(keptMedia, pendingMedia);
        await updateAdvisorProfile(
          mediaChanged
            ? { ...request, images, updateImages: true }
            : { ...request, updateImages: false },
          mediaChanged ? files : []
        );
      }
      // Post-mutation refetch through the SHARED thunk, so the advisor context
      // converges without a reload: 404 -> create -> activation enabled.
      // Rebase the editor on what came back; in particular this is what turns
      // the just-uploaded `pendingMedia` into server-backed rows, so a second
      // save does not upload the same files again. A failed GET rebases
      // nothing.
      const result = await refetch();
      adopt(result, seedIdentityAndMedia);
    } catch (error) {
      if (forCreate && isProfileAlreadyExists(error)) {
        // The row already exists (another tab, or a create whose response was
        // lost). Re-read it: adopting it switches the page to update mode.
        // When the re-read itself fails, the load-error screen offers a retry
        // and this notice is waiting once the existing row is on screen.
        const result = await refetch();
        adopt(result, seedIdentityAndMedia);
        setProfileError(PROFILE_EXISTS_NOTICE);
        return;
      }
      if (shouldRefetchAfterError(error)) {
        // A 400 we hold no code for says nothing about what the server did,
        // so re-read the truth. On a first-time create the GET usually 404s,
        // and the draft has to survive that. The editor is rebased only on a
        // create, where the re-read can change WHICH profile is on screen; in
        // update mode the advisor's edits stay put. A 400 almost always means
        // nothing was stored, and emptying the form under the error message
        // would make them retype what they can simply correct and resend.
        const result = await refetch();
        if (forCreate) {
          adopt(result, seedIdentityAndMedia);
        }
        // `result.ok` matters: a failed reconciliation GET must not be read as
        // "no profile exists".
        if (forCreate && result.ok && result.profile != null) {
          setProfileError(PROFILE_EXISTS_NOTICE);
          return;
        }
        if (!forCreate && result.ok && result.profile == null) {
          // Only NOW is "the profile is gone" a fact rather than a status code.
          // The page has switched to the create form, so the copy can point at
          // it. When the GET failed instead, the editor is still the only thing
          // on screen and this message would name a button that is not there.
          setProfileError(PROFILE_GONE_ERROR);
          return;
        }
      }
      setProfileError(mapProfileSaveError(error));
    } finally {
      setSavingProfile(false);
    }
  };

  // Saving an empty list is allowed and clears the schedule. The shared
  // editor asks for an explicit confirmation before it ever calls `onSave([])`.
  const submitHours = async (entries: AdvisorWorkingHoursEntry[]) => {
    if (savingHours || userId == null) {
      return;
    }
    setSavingHours(true);
    setHoursError(null);
    setHoursSaved(false);
    try {
      await updateAdvisorWorkingHours(userId, entries);
      // Only the hours: an identity edit in progress is none of this
      // mutation's business.
      const result = await refetch();
      adopt(result, seedHours);
      setHoursSaved(true);
    } catch (error) {
      if (shouldRefetchAfterError(error)) {
        const result = await refetch();
        adopt(result, seedHours);
        if (result.ok && result.profile == null) {
          // Create mode unmounts the hours editor, so an hours-scoped error
          // would be set and never seen. Surface it where the advisor is
          // actually looking.
          setProfileError(PROFILE_GONE_ERROR);
          return;
        }
      }
      setHoursError(mapWorkingHoursError(error));
    } finally {
      setSavingHours(false);
    }
  };

  const changeAvailability = async (available: boolean) => {
    if (savingAvailability || userId == null) {
      return;
    }
    // Belt and braces under the disabled buttons: available=true must be
    // unreachable while the activation gate is closed, however the click
    // arrives. Pause (available=false) stays exempt.
    if (available && !canSubmitAvailabilityTrue) {
      return;
    }
    setSavingAvailability(true);
    setAvailabilityError(null);
    setAvailabilityNotice(null);
    const wasInCall = profile?.availability === "IN_CALL";
    try {
      await setAdvisorAvailability(userId, available);
      // No re-seed: this mutation owns neither the identity, the media nor the
      // hours, and rebasing them here would silently discard an edit the
      // advisor has not saved yet.
      await refetch();
      if (!available && wasInCall) {
        // Pausing during a call preserves the current call and takes effect
        // when it ends.
        setAvailabilityNotice("Pauza počinje po završetku poziva.");
      }
    } catch (error) {
      setAvailabilityError(mapAvailabilityError(error));
    } finally {
      setSavingAvailability(false);
    }
  };

  const toggleArea = (areaId: number) => {
    setSelectedAreaIds((prev) =>
      prev.includes(areaId)
        ? prev.filter((id) => id !== areaId)
        : [...prev, areaId]
    );
  };

  const identityFields = useMemo(
    () =>
      [
        { key: "firstName" as const, label: "Ime", max: MAX_NAME, type: "text" },
        {
          key: "secondName" as const,
          label: "Prezime",
          max: MAX_NAME,
          type: "text",
        },
        { key: "city" as const, label: "Grad", max: MAX_CITY, type: "text" },
        {
          key: "phoneNumber" as const,
          label: "Telefon",
          max: MAX_PHONE,
          type: "tel",
        },
      ],
    []
  );

  const renderAdvisoryAreas = () => (
    <fieldset className={styles.areaGroup}>
      <legend className={styles.areaLegend}>Oblasti savetovanja</legend>
      {advisoryAreas.status === "loading" && (
        <p className={styles.hint}>Učitavanje oblasti...</p>
      )}
      {advisoryAreas.status === "error" && (
        <p className={styles.hint}>{AREAS_UNAVAILABLE}</p>
      )}
      {advisoryAreas.status === "loaded" && (
        <div className={styles.chipList}>
          {advisoryAreas.areas.map((area) => {
            const selected = selectedAreaIds.includes(area.id);
            return (
              <button
                key={area.id}
                type="button"
                className={classNames(styles.chip, {
                  [styles.chipSelected]: selected,
                })}
                aria-pressed={selected}
                disabled={savingProfile}
                onClick={() => toggleArea(area.id)}
              >
                {area.name}
              </button>
            );
          })}
        </div>
      )}
    </fieldset>
  );

  const renderIdentityForm = (forCreate: boolean) => (
    <section className={styles.card} aria-labelledby="advisor-profile-title">
      <h2 id="advisor-profile-title" className={styles.cardTitle}>
        {forCreate ? "Kreirajte profil savetnika" : "Podaci savetnika"}
      </h2>
      {forCreate && (
        <p className={styles.hint}>
          Kreirajte profil savetnika da biste primali online konsultacije.
        </p>
      )}

      {identityFields.map((field) => (
        <div key={field.key} className={styles.field}>
          <TextField
            id={`advisor-${field.key}`}
            name={`advisor-${field.key}`}
            type={field.type}
            label={field.label}
            wrapClass={styles.textFieldWrap}
            maxLength={field.max}
            value={identity[field.key]}
            disabled={savingProfile}
            onChange={(value: string) =>
              setIdentity((prev) => ({ ...prev, [field.key]: value }))
            }
          />
          {identityErrors[field.key] && (
            <span className={styles.fieldError} role="alert">
              {identityErrors[field.key]}
            </span>
          )}
        </div>
      ))}

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="advisor-biography">
          Biografija
        </label>
        <textarea
          id="advisor-biography"
          className={styles.textarea}
          rows={5}
          value={identity.biography}
          disabled={savingProfile}
          onChange={(e) =>
            setIdentity((prev) => ({ ...prev, biography: e.target.value }))
          }
        />
      </div>

      {renderAdvisoryAreas()}

      <MediaManager
        kept={keptMedia}
        pending={pendingMedia}
        disabled={savingProfile}
        onKeptChange={setKeptMedia}
        onPendingChange={setPendingMedia}
        error={mediaError}
        onError={setMediaError}
      />

      {profileError && (
        <p className={styles.error} role="alert">
          {profileError}
        </p>
      )}

      <Button
        buttonType="blue"
        disabled={savingProfile}
        onClick={() => submitProfile(forCreate)}
      >
        {savingProfile
          ? "Čuvanje..."
          : forCreate
          ? "Kreiraj profil"
          : "Sačuvaj podatke"}
      </Button>
    </section>
  );

  // Read-only: the association adds the advisor by e-mail address, and the
  // 30 s refetch picks a new one up without a refresh control.
  const renderAssociations = () => {
    const associations = profile?.associations ?? [];
    return (
      <section
        className={styles.card}
        aria-labelledby="advisor-associations-title"
      >
        <h2 id="advisor-associations-title" className={styles.cardTitle}>
          Udruženja
        </h2>
        {associations.length === 0 ? (
          <p className={styles.notice} role="note">
            {NO_ASSOCIATIONS_NOTICE}
          </p>
        ) : (
          <ul className={styles.associationList}>
            {associations.map((association) => (
              <li key={association.id} className={styles.associationItem}>
                <span className={styles.associationName}>
                  {association.name}
                </span>
                {association.city && (
                  <span className={styles.associationMeta}>
                    {association.city}
                  </span>
                )}
                {association.phoneNumber && (
                  <span className={styles.associationMeta}>
                    {association.phoneNumber}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  };

  const renderAvailabilityPanel = () => {
    if (!enrolled) {
      const blockedBySchedule = !hasSchedule;
      return (
        <section
          className={styles.card}
          aria-labelledby="advisor-availability-title"
        >
          <h2 id="advisor-availability-title" className={styles.cardTitle}>
            Dostupnost
          </h2>
          <p className={styles.hint}>Niste prijavljeni za online konsultacije.</p>
          {blockedBySchedule && (
            <p className={styles.fieldError} role="note">
              Unesite radno vreme pre aktivacije, bez rasporeda nećete biti
              dostupni.
            </p>
          )}
          {availabilityError && (
            <p className={styles.error} role="alert">
              {availabilityError}
            </p>
          )}
          <Button
            buttonType="blue"
            disabled={!canSubmitAvailabilityTrue}
            onClick={() => changeAvailability(true)}
          >
            {savingAvailability ? "Aktivacija..." : "Aktiviraj dostupnost"}
          </Button>
        </section>
      );
    }

    return (
      <section
        className={styles.card}
        aria-labelledby="advisor-availability-title"
      >
        <h2 id="advisor-availability-title" className={styles.cardTitle}>
          Dostupnost
        </h2>
        <p className={styles.statusStrip}>
          <span className={styles.statusValue}>
            {AVAILABILITY_LABEL[profile?.availability ?? ""] ?? "Nedostupan"}
          </span>
          {paused && <span className={styles.pausedBadge}>Na pauzi</span>}
        </p>
        <p className={styles.hint}>
          Dostupnost se menja prema rasporedu (do 5 minuta kašnjenja).
        </p>
        {!hasSchedule && (
          <p className={styles.fieldError} role="note">
            Nemate uneto radno vreme, nećete biti dostupni dok ga ne unesete.
          </p>
        )}
        {availabilityNotice && (
          <p className={styles.notice} role="status">
            {availabilityNotice}
          </p>
        )}
        {availabilityError && (
          <p className={styles.error} role="alert">
            {availabilityError}
          </p>
        )}
        {paused ? (
          <>
            {/* Resume shares the FULL activation gate, schedule included;
                pause never does: going unavailable is always safe. */}
            <Button
              buttonType="blue"
              disabled={!canSubmitAvailabilityTrue}
              onClick={() => changeAvailability(true)}
            >
              {savingAvailability ? "Slanje..." : "Dostupan sam"}
            </Button>
            <p className={styles.hint}>
              Nakon nastavka dostupni ste u okviru svog radnog vremena.
            </p>
          </>
        ) : (
          <Button
            buttonType="white"
            disabled={savingAvailability}
            onClick={() => changeAvailability(false)}
          >
            {savingAvailability ? "Slanje..." : "Idi na pauzu"}
          </Button>
        )}
      </section>
    );
  };

  if (userId == null) {
    return (
      <div className={styles.page}>
        <p className={styles.error}>Sesija nije dostupna.</p>
      </div>
    );
  }

  if (status === "idle" || (status === "loading" && profile == null)) {
    return (
      <div className={styles.page}>
        <p>Učitavanje profila savetnika...</p>
      </div>
    );
  }

  if (status === "error" && profile == null) {
    return (
      <div className={styles.page}>
        <p className={styles.error} role="alert">
          {GENERIC_LOAD_ERROR}
        </p>
        <Button
          buttonType="blue"
          onClick={() => {
            void refetch();
          }}
        >
          Pokušaj ponovo
        </Button>
      </div>
    );
  }

  // No profile row yet (404): the create form.
  if (status === "noProfile" || profile == null) {
    return (
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>Profil savetnika</h1>
        {renderIdentityForm(true)}
      </div>
    );
  }

  // Profile exists; enrollment decides which availability block renders.
  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Profil savetnika</h1>
      {profile.averageRate != null && (
        <p className={styles.rating}>
          {`Ocena: ${profile.averageRate.toFixed(1)} (${
            profile.numOfRates ?? 0
          })`}
        </p>
      )}
      {loadError && (
        <p className={styles.error} role="alert">
          {GENERIC_LOAD_ERROR}
        </p>
      )}
      {/* The panels are a grid, not a stack, so the profile fits on a screen.
          The page title, the rating and the load error stay outside it: they
          speak for the whole page, not for one panel. */}
      <div className={styles.panelGrid}>
        {renderAvailabilityPanel()}
        {renderAssociations()}
        {renderIdentityForm(false)}
        <WorkingHoursEditor
          draft={hoursDraft}
          onChange={setHoursDraft}
          onSave={submitHours}
          saving={savingHours}
          saveError={hoursError}
          savedNotice={hoursSaved}
          title="Radno vreme"
        />
      </div>
    </div>
  );
};

/**
 * Identity, areas, media and hours are per-account LOCAL state. `userId` comes
 * from the JWT, so keying the editor on it discards the previous advisor's
 * draft by construction: no reset effect, and no dependence on hook
 * declaration order. A reset effect declared after the profile-seeding effect
 * would seed a preloaded profile and then immediately blank it.
 */
const AdvisorProfilePage: React.FC = () => {
  const userId = useSelector(selectTokenUserId);
  return <AdvisorProfileEditor key={userId ?? "anonymous"} />;
};

export default AdvisorProfilePage;
